import { NutritionJournal, User } from '@/types';

export const syncNutritionGoalsWithProfile = (journal: NutritionJournal, user: User | null) => {
  // Nueva validación al inicio para evitar sobrescribir un weight ya sincronizado
  if (
    journal.weight &&
    user?.profile?.weight &&
    Math.abs(journal.weight - user.profile.weight) < 0.01
  ) {
    // El peso ya está sincronizado, no hagas nada
    return journal;
  }

  if (!user?.profile) return journal;

  const updatedJournal = { ...journal };
  
  // ALWAYS use the profile weight as the single source of truth
  const weight = parseFloat(String(user.profile.weight)) || 70;
  updatedJournal.weight = weight; // Direct assignment, no additional checks
  
  // Ensure all values are valid numbers
  const targetCalories = parseFloat(String(user.profile.targetCalories)) || 2000;
  // ALWAYS use 0.8 g/kg for protein calculation
  const calculatedProtein = Math.round(weight * 0.8);
  
  // Ensure we use valid numbers for all nutritional values
  updatedJournal.nutritionSummary = {
    ...updatedJournal.nutritionSummary,
    goalCalories: isNaN(targetCalories) ? 2000 : targetCalories,
    goalProtein: calculatedProtein,
    goalCarbs: parseFloat(String(user.profile.targetCarbs)) || 250,
    goalFat: parseFloat(String(user.profile.targetFat)) || 65,
    goalIron: parseFloat(String(user.profile.targetIron)) || (user.profile.gender === 'female' ? 18 : 8),
    goalCalcium: parseFloat(String(user.profile.targetCalcium)) || 1000,
    goalZinc: parseFloat(String(user.profile.targetZinc)) || 11,
    goalMagnesium: parseFloat(String(user.profile.targetMagnesium)) || 400,
    goalOmega3: parseFloat(String(user.profile.targetOmega3)) || 1.6
  };
  
  // Ensure consumed values are also valid numbers
  updatedJournal.nutritionSummary.caloriesConsumed = isNaN(Number(updatedJournal.nutritionSummary.caloriesConsumed)) 
    ? 0 : Number(updatedJournal.nutritionSummary.caloriesConsumed);
  updatedJournal.nutritionSummary.proteinConsumed = isNaN(Number(updatedJournal.nutritionSummary.proteinConsumed))
    ? 0 : Number(updatedJournal.nutritionSummary.proteinConsumed);
  updatedJournal.nutritionSummary.carbsConsumed = isNaN(Number(updatedJournal.nutritionSummary.carbsConsumed))
    ? 0 : Number(updatedJournal.nutritionSummary.carbsConsumed);
  updatedJournal.nutritionSummary.fatConsumed = isNaN(Number(updatedJournal.nutritionSummary.fatConsumed))
    ? 0 : Number(updatedJournal.nutritionSummary.fatConsumed);
  updatedJournal.nutritionSummary.ironConsumed = isNaN(Number(updatedJournal.nutritionSummary.ironConsumed))
    ? 0 : Number(updatedJournal.nutritionSummary.ironConsumed);
  updatedJournal.nutritionSummary.calciumConsumed = isNaN(Number(updatedJournal.nutritionSummary.calciumConsumed))
    ? 0 : Number(updatedJournal.nutritionSummary.calciumConsumed);
  updatedJournal.nutritionSummary.zincConsumed = isNaN(Number(updatedJournal.nutritionSummary.zincConsumed))
    ? 0 : Number(updatedJournal.nutritionSummary.zincConsumed);
  updatedJournal.nutritionSummary.magnesiumConsumed = isNaN(Number(updatedJournal.nutritionSummary.magnesiumConsumed))
    ? 0 : Number(updatedJournal.nutritionSummary.magnesiumConsumed);
  updatedJournal.nutritionSummary.omega3Consumed = isNaN(Number(updatedJournal.nutritionSummary.omega3Consumed))
    ? 0 : Number(updatedJournal.nutritionSummary.omega3Consumed);

  return updatedJournal;
};

const calculateNutritionScore = (journal: NutritionJournal, user?: User | null) => {
  // Verificar si el journal existe y tiene nutritionSummary definido
  if (!journal) {
    console.error('NUTRICION: El journal no existe');
    return { score: 50 };
  }

  const ns = journal.nutritionSummary;
  
  if (!ns) {
    console.error('NUTRICION: No hay nutrition summary, devolviendo score por defecto (50)');
    return { score: 50 };
  }

  // CRITICAL FIX: Verificar que los objetivos no estén en 0
  const hasValidGoals = ns.goalProtein > 0 && ns.goalCarbs > 0 && ns.goalFat > 0;
  
  if (!hasValidGoals) {
    console.error('NUTRICION: Objetivos inválidos detectados:', {
      goalProtein: ns.goalProtein,
      goalCarbs: ns.goalCarbs, 
      goalFat: ns.goalFat,
      goalCalories: ns.goalCalories
    });
    return { score: 50 };
  }

  // Calcular porcentajes de macronutrientes (limitados al 100%)
  function macroPercent(consumed: number = 0, goal: number = 1) {
    if (!goal || goal <= 0) {
      console.warn('NUTRICION: Goal es 0 o negativo para macro:', { consumed, goal });
      return 0;
    }
    const percent = Math.min(100, Math.max(0, (consumed / goal) * 100));
    console.log(`NUTRICION: Macro calculation: ${consumed}/${goal} = ${percent.toFixed(1)}%`);
    return percent;
  }
  
  const proteinPercent = macroPercent(
    typeof ns.proteinConsumed === 'number' ? ns.proteinConsumed : Number(ns.proteinConsumed || 0), 
    typeof ns.goalProtein === 'number' ? ns.goalProtein : Number(ns.goalProtein || 0)
  );
  
  const carbsPercent = macroPercent(
    typeof ns.carbsConsumed === 'number' ? ns.carbsConsumed : Number(ns.carbsConsumed || 0), 
    typeof ns.goalCarbs === 'number' ? ns.goalCarbs : Number(ns.goalCarbs || 0)
  );
  
  const fatPercent = macroPercent(
    typeof ns.fatConsumed === 'number' ? ns.fatConsumed : Number(ns.fatConsumed || 0), 
    typeof ns.goalFat === 'number' ? ns.goalFat : Number(ns.goalFat || 0)
  );
  
  // Calcular promedio de macronutrientes - SIEMPRE incluir los 3
  const macrosAvgPercent = (proteinPercent + carbsPercent + fatPercent) / 3;
  
  // Calcular porcentajes de micronutrientes (limitados al 100%)
  function microPercent(consumed: number = 0, goal: number = 1) {
    if (!goal || goal <= 0) {
      console.warn('NUTRICION: Goal es 0 o negativo para micro:', { consumed, goal });
      return 0;
    }
    const percent = Math.min(100, Math.max(0, (consumed / goal) * 100));
    console.log(`NUTRICION: Micro calculation: ${consumed}/${goal} = ${percent.toFixed(1)}%`);
    return percent;
  }
  
  const ironPercent = microPercent(
    typeof ns.ironConsumed === 'number' ? ns.ironConsumed : Number(ns.ironConsumed || 0), 
    typeof ns.goalIron === 'number' ? ns.goalIron : Number(ns.goalIron || 8)
  );
  
  const calciumPercent = microPercent(
    typeof ns.calciumConsumed === 'number' ? ns.calciumConsumed : Number(ns.calciumConsumed || 0), 
    typeof ns.goalCalcium === 'number' ? ns.goalCalcium : Number(ns.goalCalcium || 1000)
  );
  
  const zincPercent = microPercent(
    typeof ns.zincConsumed === 'number' ? ns.zincConsumed : Number(ns.zincConsumed || 0), 
    typeof ns.goalZinc === 'number' ? ns.goalZinc : Number(ns.goalZinc || 11)
  );
  
  const magnesiumPercent = microPercent(
    typeof ns.magnesiumConsumed === 'number' ? ns.magnesiumConsumed : Number(ns.magnesiumConsumed || 0), 
    typeof ns.goalMagnesium === 'number' ? ns.goalMagnesium : Number(ns.goalMagnesium || 400)
  );
  
  const omega3Percent = microPercent(
    typeof ns.omega3Consumed === 'number' ? ns.omega3Consumed : Number(ns.omega3Consumed || 0), 
    typeof ns.goalOmega3 === 'number' ? ns.goalOmega3 : Number(ns.goalOmega3 || 1.6)
  );
  
  // Calcular promedio de micronutrientes - SIEMPRE incluir los 5
  const microsAvgPercent = (ironPercent + calciumPercent + zincPercent + magnesiumPercent + omega3Percent) / 5;
  
  // FÓRMULA FINAL: 70% macros + 30% micros
  const macrosComponent = macrosAvgPercent * 0.7;
  const microsComponent = microsAvgPercent * 0.3;
  const nutritionScore = Math.round(macrosComponent + microsComponent);
  
  // Logging detallado para debug
  console.log('NUTRICIÓN - CÁLCULO DETALLADO COMPLETO:', {
    dataValidity: {
      hasValidGoals,
      goalProtein: ns.goalProtein,
      goalCarbs: ns.goalCarbs,
      goalFat: ns.goalFat
    },
    rawValues: {
      consumed: {
        protein: ns.proteinConsumed,
        carbs: ns.carbsConsumed,
        fat: ns.fatConsumed,
        iron: ns.ironConsumed,
        calcium: ns.calciumConsumed,
        zinc: ns.zincConsumed,
        magnesium: ns.magnesiumConsumed,
        omega3: ns.omega3Consumed
      },
      goals: {
        protein: ns.goalProtein,
        carbs: ns.goalCarbs,
        fat: ns.goalFat,
        iron: ns.goalIron,
        calcium: ns.goalCalcium,
        zinc: ns.goalZinc,
        magnesium: ns.goalMagnesium,
        omega3: ns.goalOmega3
      }
    },
    macronutrientes: {
      proteína: `${proteinPercent.toFixed(1)}%`,
      carbohidratos: `${carbsPercent.toFixed(1)}%`,
      grasas: `${fatPercent.toFixed(1)}%`,
      promedio: `${macrosAvgPercent.toFixed(1)}%`,
      componente70: `${macrosComponent.toFixed(1)}`
    },
    micronutrientes: {
      hierro: `${ironPercent.toFixed(1)}%`,
      calcio: `${calciumPercent.toFixed(1)}%`,
      zinc: `${zincPercent.toFixed(1)}%`,
      magnesio: `${magnesiumPercent.toFixed(1)}%`,
      omega3: `${omega3Percent.toFixed(1)}%`,
      promedio: `${microsAvgPercent.toFixed(1)}%`,
      componente30: `${microsComponent.toFixed(1)}`
    },
    fórmulaFinal: {
      macrosComponent: macrosComponent.toFixed(1),
      microsComponent: microsComponent.toFixed(1),
      suma: `${macrosComponent.toFixed(1)} + ${microsComponent.toFixed(1)} = ${nutritionScore}`,
      scoreNutriciónFinal: nutritionScore
    },
    verificación: 'Si macros=100% y micros=100%, entonces: 100*0.7 + 100*0.3 = 70 + 30 = 100'
  });

  return {
    score: nutritionScore,
    macrosAvgPercent,
    microsAvgPercent
  };
};

const calculateMovementScore = (journal: NutritionJournal) => {
  // Steps score
  const steps = journal.steps || 0;
  const stepsGoal = 10000;
  const stepsScore = Math.min(100, Math.round((steps / stepsGoal) * 100));
  
  // Workout score
  const workout = journal.workout || { minutes: 0, intensity: 'medium' };
  const minutes = workout.minutes || 0;
  
  // Intensity multiplier
  const intensityMultiplier = 
    workout.intensity === 'high' ? 1.5 :
    workout.intensity === 'medium' ? 1.0 :
    0.7;
  
  // Calculate effective minutes
  const effectiveMinutes = minutes * intensityMultiplier;
  
  // Daily workout goal (30 minutes of medium intensity)
  const workoutGoal = 30;
  
  // Workout score
  const workoutScore = Math.min(100, Math.round((effectiveMinutes / workoutGoal) * 100));
  
  // Overall movement score (60% steps, 40% workout)
  const movementScore = Math.round(stepsScore * 0.6 + workoutScore * 0.4);
  
  return {
    score: movementScore,
    stepsScore,
    workoutScore
  };
};

const calculateEnergyRestScore = (journal: NutritionJournal) => {
  // CRITICAL FIX: When energy level is perfect (10), we always want to return 100
  // This takes priority over any other calculation
  const energyLevel = journal.energyLevel || 5;
  
  if (energyLevel === 10) {
    console.log("PERFECT ENERGY VALUE (10) DETECTED - Forcing 100 score regardless of rest");
    return {
      score: 100,
      restScore: 100, // We also force rest score to be perfect
      energyScore: 100
    };
  }
  
  // For non-perfect energy levels, calculate normally
  const restLevel = journal.restLevel || 5;
  
  // IMPORTANT: Check perfect score condition for the combined optimal case
  if (energyLevel === 10 && restLevel === 8) {
    console.log("PERFECT ENERGY/REST VALUES DETECTED - Returning exact 100 score");
    return {
      score: 100,
      restScore: 100,
      energyScore: 100
    };
  }
  
  // For non-perfect cases, calculate normally
  // Rest score (1-10 scale, 8 is optimal)
  const optimumRest = 8;
  const restDifference = Math.abs(restLevel - optimumRest);
  
  // When restLevel is optimal (8), we want it to be 100%
  // For each point away from 8, we reduce by 12.5% to allow full 100% scale
  const restScore = Math.max(0, 100 - (restDifference * 12.5));
  
  // Energy score (1-10 scale, using full range 0-100%)
  // Full linear scale: energyLevel 1 = 10%, energyLevel 10 = 100%
  const energyScore = energyLevel * 10;
  
  console.log("Raw Energy Calculation:", {
    energyLevel,
    calculationFormula: "energyLevel * 10",
    computation: `${energyLevel} * 10 = ${energyScore}`,
    result: energyScore,
    fullRange: "1-10 → 10-100%"
  });
  
  // Calculate the energy/rest score with precise 50/50 weighting
  // Using direct calculation without rounding intermediate values
  const energyComponent = energyScore * 0.5;
  const restComponent = restScore * 0.5;
  const energyRestScore = Math.round(energyComponent + restComponent);
  
  console.log("Complete Energy/Rest Calculation:", {
    energyLevel,
    energyScoreRaw: energyLevel * 10,
    energyScoreFinal: energyScore,
    restLevel,
    optimalRestLevel: 8,
    restScoreCalculation: `100 - (${restDifference} * 12.5) = ${restScore}`,
    energyScore,
    restScore,
    components: {
      energyComponent: `${energyScore} * 0.5 = ${energyComponent}`,
      restComponent: `${restScore} * 0.5 = ${restComponent}`
    },
    formula: `${energyComponent} + ${restComponent}`,
    rawResult: energyComponent + restComponent,
    roundedResult: energyRestScore,
    finalScore: energyRestScore,
    perfectCheck: `energy=${energyLevel}, rest=${restLevel}, should be 100? ${energyLevel === 10 && restLevel === 8}`
  });
  
  return {
    score: energyRestScore,
    restScore,
    energyScore
  };
};

const calculateHydrationSunScore = (journal: NutritionJournal) => {
  // Water intake score (based on 6 glasses per day being optimal)
  const waterIntake = journal.waterIntake || 0;
  const waterGoal = 6;
  const waterScore = Math.min(100, Math.round((waterIntake / waterGoal) * 100));
  
  // Sun exposure score (based on 20 minutes per day being optimal)
  const sunExposure = journal.sunExposure || 0;
  const sunGoal = 20;
  const sunScore = Math.min(100, Math.round((sunExposure / sunGoal) * 100));
  
  // Overall hydration/sun score
  const hydrationSunScore = Math.round(waterScore * 0.7 + sunScore * 0.3);
  
  return {
    score: hydrationSunScore,
    waterScore,
    sunScore
  };
};

const calculateWeightBmiScore = (journal: NutritionJournal, user: User | null) => {
  // If user or required data is missing, return default score
  if (!user?.profile) {
    return { score: 50 };
  }
  
  // ALWAYS use the profile weight as the single source of truth
  const weight = Number(user.profile.weight);
  const height = Number(user.profile.height);
  
  if (!height || height <= 0) {
    return { score: 50 };
  }
  
  if (!weight || weight <= 0) {
    return { score: 50 };
  }
  
  const heightInMeters = height / 100;
  const bmi = weight / (heightInMeters * heightInMeters);
  
  // Calculate BMI score based on health ranges
  let bmiScore = 0;
  
  if (bmi < 16) {
    bmiScore = 20; // Severe underweight - more severe penalty
  } else if (bmi < 18.5) {
    bmiScore = 40; // Underweight - increased penalty
  } else if (bmi <= 24.9) {
    bmiScore = 100; // Normal weight - optimal
  } else if (bmi <= 29.9) {
    bmiScore = 40; // Overweight - increased penalty
  } else if (bmi <= 34.9) {
    bmiScore = 20; // Obesity Class I - more severe penalty
  } else if (bmi <= 39.9) {
    bmiScore = 10; // Obesity Class II - severe penalty
  } else {
    bmiScore = 0; // Obesity Class III - maximum penalty
  }
  
  // Increase impact of weight component in total score
  const weightScore = bmiScore;
  
  return {
    score: weightScore,
    bmiScore,
    bmi
  };
};

export const storeVitalScoreHistory = (date: string, score: number) => {
  try {
    // Validate score to prevent NaN
    const validScore = isNaN(score) ? 50 : Math.max(0, Math.min(100, score));
    
    const history = getVitalScoreHistory();
    history[date] = validScore;
    
    // Only keep the last 30 days
    const dates = Object.keys(history).sort();
    if (dates.length > 30) {
      const datesToRemove = dates.slice(0, dates.length - 30);
      datesToRemove.forEach(date => {
        delete history[date];
      });
    }
    
    localStorage.setItem('vitalScoreHistory', JSON.stringify(history));
    
    // Also trigger a storage event for other tabs
    try {
      const storageEvent = new StorageEvent('storage', {
        key: 'vitalScoreHistory',
        newValue: JSON.stringify(history),
        url: window.location.href
      });
      window.dispatchEvent(storageEvent);
    } catch (error) {
      console.error('Error dispatching storage event:', error);
    }
    
    return history;
  } catch (error) {
    console.error('Error storing vital score history:', error);
    return {};
  }
};

export const getVitalScoreHistory = () => {
  try {
    const history = localStorage.getItem('vitalScoreHistory');
    const parsedHistory = history ? JSON.parse(history) : {};
    
    // Validate each score in the history
    Object.entries(parsedHistory).forEach(([date, score]) => {
      if (typeof score !== 'number' || isNaN(score)) {
        parsedHistory[date] = 50; // Default to 50 for invalid scores
      }
    });
    
    return parsedHistory;
  } catch (error) {
    console.error('Error getting vital score history:', error);
    return {};
  }
};

export const formatVitalScoreHistoryForChart = (history: Record<string, number>) => {
  const chartData = Object.keys(history)
    .sort()
    .map(date => ({
      date,
      score: history[date]
    }));
  
  return chartData;
};

export const getMicronutrientStatusMessage = (journal: NutritionJournal) => {
  if (!journal.nutritionSummary) {
    return "No hay datos suficientes para evaluar tus micronutrientes.";
  }
  
  const ns = journal.nutritionSummary;
  
  // Check if there are any nutrition consumed without requiring meals
  const hasConsumedFood = ns.caloriesConsumed > 0 || 
                          ns.proteinConsumed > 0 || 
                          ns.carbsConsumed > 0 || 
                          ns.fatConsumed > 0;
  
  if (!hasConsumedFood) {
    return "Aún no has registrado comidas hoy. Añade tus comidas para ver tu progreso nutricional.";
  }
  
  const lowMicronutrients = [];
  
  if (ns.goalIron > 0 && ns.ironConsumed / ns.goalIron < 0.7) {
    lowMicronutrients.push("hierro");
  }
  
  if (ns.goalCalcium > 0 && ns.calciumConsumed / ns.goalCalcium < 0.7) {
    lowMicronutrients.push("calcio");
  }
  
  if (ns.goalZinc > 0 && ns.zincConsumed / ns.goalZinc < 0.7) {
    lowMicronutrients.push("zinc");
  }
  
  if (ns.goalMagnesium > 0 && ns.magnesiumConsumed / ns.goalMagnesium < 0.7) {
    lowMicronutrients.push("magnesio");
  }
  
  if (ns.goalOmega3 > 0 && ns.omega3Consumed / ns.goalOmega3 < 0.7) {
    lowMicronutrients.push("omega-3");
  }
  
  if (lowMicronutrients.length === 0) {
    return "¡Excelente! Estás consumiendo bien tus micronutrientes esenciales.";
  } else if (lowMicronutrients.length === 1) {
    return `Deberías aumentar tu consumo de ${lowMicronutrients[0]}.`;
  } else {
    return `Deberías enfocarte en aumentar tu consumo de ${lowMicronutrients.join(' y ')}.`;
  }
};

export const getCalorieStatusMessage = (journal: NutritionJournal) => {
  if (!journal.nutritionSummary || !journal.nutritionSummary.goalCalories) {
    return "No hay datos suficientes para evaluar tu consumo calórico.";
  }
  
  const ns = journal.nutritionSummary;
  
  // Check if there is any nutrition consumed without requiring meals
  const hasConsumedFood = ns.caloriesConsumed > 0 || 
                          ns.proteinConsumed > 0 || 
                          ns.carbsConsumed > 0 || 
                          ns.fatConsumed > 0;
  
  if (!hasConsumedFood) {
    return "Aún no has registrado comidas hoy. Añade tus comidas para ver tu progreso calórico.";
  }
  
  const caloriePercentage = (ns.caloriesConsumed / ns.goalCalories) * 100;
  
  if (caloriePercentage < 50) {
    return "Tu consumo calórico es muy bajo. Intenta comer más para alcanzar tus objetivos nutricionales.";
  } else if (caloriePercentage < 80) {
    return "Tu consumo calórico está por debajo de lo recomendado. Considera agregar más calorías saludables a tu dieta.";
  } else if (caloriePercentage >= 80 && caloriePercentage <= 110) {
    return "¡Perfecto! Tu consumo calórico está en el rango ideal.";
  } else if (caloriePercentage <= 130) {
    return "Tu consumo calórico está ligeramente por encima de lo recomendado. Vigila tu ingesta para mantenerte en tus objetivos.";
  } else {
    return "Tu consumo calórico es significativamente mayor de lo recomendado. Considera reducir tu ingesta para alcanzar tus objetivos.";
  }
};

// Completely disabling the cache to ensure fresh calculations always
export const calculateVitalScore = (journal: NutritionJournal, user: User | null, defaultScore: number = 50) => {
  try {
    if (!journal) {
      console.error('VITALSCORE: No hay journal');
      return {
        total: defaultScore,
        nutrition: { score: defaultScore },
        movement: { score: defaultScore, stepsScore: defaultScore },
        energyRest: { score: defaultScore, restScore: defaultScore, energyScore: defaultScore },
        hydrationSun: { score: defaultScore, waterScore: defaultScore, sunScore: defaultScore },
        weightBmi: { score: defaultScore }
      };
    }

    // Verificar que nutritionSummary existe
    if (!journal.nutritionSummary) {
      console.error('VITALSCORE: Journal no tiene nutritionSummary');
      journal.nutritionSummary = {
        caloriesConsumed: 0,
        goalCalories: 0,
        proteinConsumed: 0,
        goalProtein: 0,
        carbsConsumed: 0,
        goalCarbs: 0,
        fatConsumed: 0,
        goalFat: 0,
        ironConsumed: 0,
        goalIron: 0,
        calciumConsumed: 0,
        goalCalcium: 0,
        zincConsumed: 0,
        goalZinc: 0,
        magnesiumConsumed: 0,
        goalMagnesium: 0,
        vitaminCConsumed: 0,
        goalVitaminC: 0,
        omega3Consumed: 0,
        goalOmega3: 0
      };
    }

    // Detailed log of energy/rest source values for debugging
    console.log("VITAL SCORE - INPUT VALUES FOR ENERGY/REST:", {
      energyLevel: journal.energyLevel,
      restLevel: journal.restLevel,
      energyLevelRaw: typeof journal.energyLevel,
      restLevelRaw: typeof journal.restLevel,
      shouldBePerfect: journal.energyLevel === 10,
      journalData: `${JSON.stringify({energyLevel: journal.energyLevel, restLevel: journal.restLevel})}`,
    });

    // Calculate individual scores - NO CACHING
    const nutritionScore = calculateNutritionScore(journal, user);
    const movementScore = calculateMovementScore(journal);
    const energyRestScore = calculateEnergyRestScore(journal);
    const hydrationSunScore = calculateHydrationSunScore(journal);
    const weightBmiScore = calculateWeightBmiScore(journal, user);

    // Super detailed log for the energy/rest score calculation
    console.log("VITAL SCORE - ENERGY CALCULATION RESULT:", {
      energyLevel: journal.energyLevel,
      restLevel: journal.restLevel,
      calculatedEnergyScore: energyRestScore.energyScore,
      calculatedRestScore: energyRestScore.restScore,
      finalScore: energyRestScore.score,
      isPerfectScoreCase: journal.energyLevel === 10,
      scoreIs100: energyRestScore.score === 100,
      verification: `Energy=${journal.energyLevel}, Rest=${journal.restLevel}, Score=${energyRestScore.score}`
    });

    // Validate all scores
    const validNutritionScore = isNaN(nutritionScore.score) ? defaultScore : nutritionScore.score;
    const validMovementScore = isNaN(movementScore.score) ? defaultScore : movementScore.score;
    const validEnergyRestScore = isNaN(energyRestScore.score) ? defaultScore : energyRestScore.score;
    const validHydrationSunScore = isNaN(hydrationSunScore.score) ? defaultScore : hydrationSunScore.score;
    const validWeightBmiScore = isNaN(weightBmiScore.score) ? defaultScore : weightBmiScore.score;

    // Increase weight/BMI impact on total score (25% instead of 15%)
    const calculatedTotal = Math.round(
      validNutritionScore * 0.20 +
      validMovementScore * 0.20 +
      validEnergyRestScore * 0.20 +
      validHydrationSunScore * 0.15 +
      validWeightBmiScore * 0.25
    );
    
    const totalScore = isNaN(calculatedTotal) ? defaultScore : Math.max(0, Math.min(100, calculatedTotal));

    const result = {
      total: totalScore,
      nutrition: { ...nutritionScore, score: validNutritionScore },
      movement: { ...movementScore, score: validMovementScore },
      energyRest: { ...energyRestScore, score: validEnergyRestScore },
      hydrationSun: { ...hydrationSunScore, score: validHydrationSunScore },
      weightBmi: { ...weightBmiScore, score: validWeightBmiScore }
    };

    // Log final result to verify energy/rest score is correct
    console.log("VITAL SCORE - FINAL RESULT:", {
      energyRestComponent: {
        energyLevel: journal.energyLevel,
        restLevel: journal.restLevel,
        calculatedScore: validEnergyRestScore,
        isPerfect: journal.energyLevel === 10
      },
      allComponents: {
        nutrition: validNutritionScore,
        movement: validMovementScore,
        energyRest: validEnergyRestScore,
        hydrationSun: validHydrationSunScore,
        weightBmi: validWeightBmiScore
      },
      totalScore
    });

    // NO CACHING - always return fresh result
    return result;
  } catch (error) {
    console.error("Error calculating vital score:", error);
    return {
      total: defaultScore,
      nutrition: { score: defaultScore },
      movement: { score: defaultScore, stepsScore: defaultScore },
      energyRest: { score: defaultScore, restScore: defaultScore, energyScore: defaultScore },
      hydrationSun: { score: defaultScore, waterScore: defaultScore, sunScore: defaultScore },
      weightBmi: { score: defaultScore }
    };
  }
};
